import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { assessmentsApi, type MCQQuestion, type UpsertMCQQuestionRequest } from '@/lib/api/assessments';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Props {
  assessmentID: string;
  assessmentTitle: string;
}

const BLANK_FORM: UpsertMCQQuestionRequest = {
  question_text: '',
  options: ['', '', '', ''],
  correct_option: 0,
  sort_order: 0,
  points: 1,
};

const LETTERS = ['A', 'B', 'C', 'D'];

export function MCQQuestionBuilder({ assessmentID, assessmentTitle }: Props) {
  const [questions, setQuestions] = useState<MCQQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingID, setEditingID] = useState<string | null>(null);
  const [form, setForm] = useState<UpsertMCQQuestionRequest>(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingID, setDeletingID] = useState<string | null>(null);

  useEffect(() => {
    assessmentsApi.listQuestions(assessmentID)
      .then(setQuestions)
      .catch(() => toast.error('Failed to load questions'))
      .finally(() => setLoading(false));
  }, [assessmentID]);

  const handleSave = async () => {
    if (!form.question_text.trim()) { toast.error('Question text is required'); return; }
    const filledOptions = form.options.filter(o => o.trim() !== '');
    if (filledOptions.length < 2) { toast.error('At least 2 options are required'); return; }
    const payload: UpsertMCQQuestionRequest = {
      ...form,
      options: form.options.filter(o => o.trim() !== ''),
      sort_order: editingID ? form.sort_order : questions.length,
    };
    setSaving(true);
    try {
      if (editingID) {
        const updated = await assessmentsApi.updateQuestion(assessmentID, editingID, payload);
        setQuestions(prev => prev.map(q => q.id === editingID ? updated : q));
        toast.success('Question updated');
      } else {
        const created = await assessmentsApi.createQuestion(assessmentID, payload);
        setQuestions(prev => [...prev, created]);
        toast.success('Question added');
      }
      setShowForm(false); setEditingID(null); setForm(BLANK_FORM);
    } catch { toast.error('Failed to save question'); }
    finally { setSaving(false); }
  };

  const handleEdit = (q: MCQQuestion) => {
    setEditingID(q.id);
    setForm({
      question_text: q.question_text,
      options: [...q.options, ...Array(Math.max(0, 4 - q.options.length)).fill('')],
      correct_option: q.correct_option,
      sort_order: q.sort_order,
      points: q.points,
    });
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    setDeletingID(id);
  };

  const confirmDelete = async () => {
    if (!deletingID) return;
    const id = deletingID;
    try {
      await assessmentsApi.deleteQuestion(assessmentID, id);
      setQuestions(prev => prev.filter(q => q.id !== id));
      toast.success('Question deleted');
    } catch { toast.error('Failed to delete question'); }
    finally { setDeletingID(null); }
  };

  if (loading) return <div className="px-4 py-3 text-sm text-[#6b7280]">Loading questions…</div>;

  return (
    <div className="mt-2 mb-4 ml-4 border-l-2 border-[#ffd9c7] pl-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-sm font-semibold">{assessmentTitle}</span>
          <span className="ml-2 text-xs text-[#6b7280]">{questions.length} question{questions.length !== 1 ? 's' : ''}</span>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingID(null); setForm(BLANK_FORM); }}
          className="text-xs text-[#ff5a1f] dark:text-blue-400 hover:underline"
        >
          + Add Question
        </button>
      </div>

      {questions.length === 0 && !showForm && (
        <p className="text-xs text-[#6b7280] py-2">No questions yet. Add the first one.</p>
      )}

      <div className="space-y-2">
        {questions.map((q, idx) => (
          <div key={q.id} className="rounded-lg border border-[#ececec] bg-[#f3f4f6]/20 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-2">Q{idx + 1}. {q.question_text}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {q.options.map((opt, i) => (
                    <div key={i} className={`text-xs px-2 py-1 rounded ${i === q.correct_option ? 'bg-green-500/10 border border-green-500/30 text-green-600 dark:text-green-400' : 'bg-[#f3f4f6] text-[#6b7280]'}`}>
                      {LETTERS[i]}. {opt}{i === q.correct_option ? ' ✓' : ''}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#6b7280] mt-1.5">{q.points} pt{q.points !== 1 ? 's' : ''}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => handleEdit(q)} className="text-xs text-[#6b7280] hover:text-[#111827]">Edit</button>
                <button onClick={() => handleDelete(q.id)} className="text-xs text-[#6b7280] hover:text-destructive">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="mt-3 rounded-lg border border-[#ffd9c7] bg-[#f3f4f6]/10 p-4">
          <p className="text-sm font-semibold mb-3">{editingID ? 'Edit Question' : 'New Question'}</p>
          <div className="mb-3">
            <label className="text-xs text-[#6b7280] block mb-1">Question Text</label>
            <textarea
              value={form.question_text}
              onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))}
              rows={2}
              className="w-full rounded border border-[#ececec] bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#ff5a1f]"
            />
          </div>
          <div className="mb-3">
            <label className="text-xs text-[#6b7280] block mb-2">Options (select the correct one)</label>
            <div className="space-y-1.5">
              {form.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="radio" name="correct" checked={form.correct_option === i} onChange={() => setForm(f => ({ ...f, correct_option: i }))} className="accent-[#ff5a1f] flex-shrink-0" />
                  <span className="text-xs text-[#6b7280] w-5 flex-shrink-0">{LETTERS[i]}.</span>
                  <input
                    type="text"
                    value={opt}
                    onChange={e => { const o = [...form.options]; o[i] = e.target.value; setForm(f => ({ ...f, options: o })); }}
                    placeholder={`Option ${LETTERS[i]}`}
                    className="flex-1 rounded border border-[#ececec] bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#ff5a1f]"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="mb-4 flex items-center gap-3">
            <label className="text-xs text-[#6b7280]">Points</label>
            <input type="number" min={1} value={form.points} onChange={e => setForm(f => ({ ...f, points: parseInt(e.target.value, 10) || 1 }))} className="w-16 rounded border border-[#ececec] bg-white px-2 py-1 text-xs focus:outline-none" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setEditingID(null); }} className="text-xs px-3 py-1.5 rounded border border-[#ececec] text-[#6b7280] hover:text-[#111827]">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="text-xs px-3 py-1.5 rounded bg-[#ff5a1f] text-white font-medium disabled:opacity-60">
              {saving ? 'Saving…' : editingID ? 'Update' : 'Add Question'}
            </button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deletingID} onOpenChange={(open) => !open && setDeletingID(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this question?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
